import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var EventHub = /*#__PURE__*/function () {
  function EventHub() {
    _classCallCheck(this, EventHub);
    // Ignored via go/ees005
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _defineProperty(this, "subscribers", new Map());
    this.subscribers = new Map();
  }

  /**
   * Subscribes to a specific Editor AI event, registering a callback function that will be invoked whenever the event is published.
   *
   * @param {Object} params - The subscription parameters.
   * @param {T} params.event - The name of the event to subscribe to.
   * @param {(data: EventDataMap[T]) => void} params.callback - The callback function to invoke when the event is published.
   * It receives the event data as its parameter.
   * @returns {Object} An object containing the `unsubscribe` method to remove the subscription when it's no longer needed.
   *
   * @example
   * // Subscribing to an event
   * const subscription = eventHub.subscribe({
   *   event: 'start prompt',
   *   callback: (data) => {
   *     console.log('Received start prompt event:', data);
   *   },
   * });
   *
   * // Later, to unsubscribe
   * subscription.unsubscribe();
   */
  return _createClass(EventHub, [{
    key: "subscribe",
    value: function subscribe(_ref) {
      var _this = this;
      var event = _ref.event,
        callback = _ref.callback;
      var callbacks = this.subscribers.get(event) || [];
      this.subscribers.set(event, [].concat(_toConsumableArray(callbacks), [callback]));
      return {
        unsubscribe: function unsubscribe() {
          var _this$subscribers$get;
          var callbacks = ((_this$subscribers$get = _this.subscribers.get(event)) === null || _this$subscribers$get === void 0 ? void 0 : _this$subscribers$get.filter(function (sub) {
            return sub !== callback;
          })) || [];
          if (callbacks.length > 0) {
            _this.subscribers.set(event, callbacks);
          } else {
            _this.subscribers.delete(event);
          }
        }
      };
    }

    /**
     * Publishes an Editor AI event to a target editor. (This will usually be an event like 'start prompt').
     *
     * @param {Object} params - The event parameters.
     * @param {T} params.event - The name of the event to publish.
     * @param {ToEditorEventDataMap[T]} params.data - The data associated with the event, specific to the event type.
     * @returns {void}
     *
     * @example
     * // Publishing an event to the editor
     * eventHub.publishToEditor({
     *   event: 'start prompt',
     *   data: {
     *     prompt: 'issue reformatter',
     *     targetEditorId,
     *     analyticSourceId: 'jiraIssueReformatterExternalButton',
     *   },
     * });
     */
  }, {
    key: "publishToEditor",
    value: function publishToEditor(_ref2) {
      var event = _ref2.event,
        data = _ref2.data;
      var callbacks = this.subscribers.get(event) || [];
      callbacks.forEach(function (callback) {
        callback(data);
      });
    }

    /**
     * Publishes an event originating from the editor, invoking all registered subscribers for that event.
     *
     * @param {Object} params - The event parameters.
     * @param {T} params.event - The name of the event to publish.
     * @param {FromEditorEventDataMap[T]} params.data - The data associated with the event, specific to the event type.
     * @returns {void}
     *
     * @example
     * // Publishing an event from the editor
     * eventHub.publishFromEditor({
     *   event: 'replace title in confluence',
     *   data: {
     *     analyticSourceId: 'confluenceTitleToolbar',
     *     sourceEditorId,
     *     title: textContent,
     *   },
     * });
     */
  }, {
    key: "publishFromEditor",
    value: function publishFromEditor(_ref3) {
      var event = _ref3.event,
        data = _ref3.data;
      var callbacks = this.subscribers.get(event) || [];
      callbacks.forEach(function (callback) {
        callback(data);
      });
    }
  }]);
}();
var eventHub = new EventHub();
export { eventHub };